import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import SectionHeading from "@/components/SectionHeading";
import { LifeBuoy, Phone, MessageCircle, Building2, BellRing, ShieldQuestion, ArrowUpRight, ArrowRight } from "lucide-react";

const TRACKS = [
  { icon: ShieldQuestion, title: "Basic Cyber Guidance",
    desc: "Plain-language guidance for individuals, families and small teams confused by suspicious calls, messages or screenshots." },
  { icon: BellRing, title: "Awareness Support",
    desc: "Pre-built awareness assets and short consultations for RWAs, schools and community groups running their own programs." },
  { icon: Phone, title: "Reporting Assistance",
    desc: "Step-by-step support to file 1930 reports, complete cybercrime.gov.in submissions and prepare bank-side documentation." },
  { icon: MessageCircle, title: "Digital Safety Help",
    desc: "Email-based assistance for victims of phishing, OTP fraud, romance scams and account takeover — what to do in the first 24 hours." },
  { icon: Building2, title: "Enterprise Consultation",
    desc: "Short, focused 30–60 minute calls for businesses needing a second opinion on incidents, audits or vendor decisions." },
  { icon: LifeBuoy, title: "Citizen Helpdesk",
    desc: "Pro-bono triage for genuine victims directed to us via our awareness partner networks." },
];

export default function Assistance() {
  return (
    <div data-testid="assistance-page">
      <PageHero
        eyebrow="Division 05 · Cyber Assistance"
        title="Digital safety response — for citizens and small teams"
        description="Cyber Assistance is the human side of Cosmos Defence — a structured way for citizens, small businesses and awareness partners to get guidance, reporting help and recovery support."
        accent="purple"
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact?type=support" className="btn-accent" data-testid="assistance-hero-cta">
            Get Assistance <ArrowUpRight size={16} />
          </Link>
          <a href="tel:1930" className="btn-ghost" data-testid="assistance-1930-cta">
            Call 1930 <Phone size={14} />
          </a>
        </div>
      </PageHero>

      <section className="py-20 bg-white">
        <div className="container-cd">
          <SectionHeading
            eyebrow="Assistance Tracks"
            title="What we help with"
            description="Cyber Assistance is not a substitute for the 1930 helpline — it complements it with guidance, awareness materials and structured triage."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TRACKS.map((t, i) => (
              <div key={t.title} className="card-cd" data-testid={`assistance-track-${i}`}>
                <span className="h-12 w-12 grid place-items-center rounded-xl bg-gradient-to-br from-rose-500/10 to-pink-500/10 border border-rose-100 text-rose-600">
                  <t.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-[#0A1B5E]">{t.title}</h3>
                <p className="mt-3 text-slate-600 text-[15px] leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 1930 prominent block */}
      <section className="py-12 bg-[#F8FAFC]">
        <div className="container-cd">
          <div className="card-cd !p-10 ring-glow-cyan border-cyan-200 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
            <div>
              <BellRing size={28} className="text-[#6D28D9]" />
              <h3 className="mt-3 font-display text-2xl font-bold text-[#0A1B5E]">Victim of Cyber Fraud? Report Immediately on 1930</h3>
              <p className="mt-2 text-slate-600 max-w-2xl">
                The faster you report, the higher the chance of recovery. Call the national cybercrime helpline first, then file a detailed report at cybercrime.gov.in. We can help you prepare that report.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a href="tel:1930" className="btn-accent" data-testid="assistance-1930-call">Call 1930 <Phone size={14} /></a>
              <Link to="/contact?type=support" className="btn-ghost" data-testid="assistance-help-prepare">Help me prepare my report <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
